import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  BanknotesIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CubeIcon,
  GlobeAltIcon,
  BeakerIcon,
  CpuChipIcon,
  TruckIcon,
  ShoppingBagIcon,
  SunIcon,
  HeartIcon,
  WrenchScrewdriverIcon,
  RocketLaunchIcon,
  CircleStackIcon,
  DevicePhoneMobileIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

function Portfolio() {
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const industries = [
    {
      id: 'tech',
      name: 'Technology',
      icon: CpuChipIcon,
      percentage: 25,
      description: 'Leading tech companies and emerging startups in AI, cloud computing, and cybersecurity.',
      companies: ['Artificial Intelligence', 'Cloud Services', 'Cybersecurity', '5G Networks'],
      color: 'bg-primary-500'
    },
    {
      id: 'finance',
      name: 'Financial Services',
      icon: BanknotesIcon,
      percentage: 20,
      description: 'Digital payment solutions, fintech innovations, and blockchain technology.',
      companies: ['Digital Banking', 'Payment Processing', 'Blockchain', 'Insurance Tech'],
      color: 'bg-secondary-500'
    },
    {
      id: 'realestate',
      name: 'Real Estate',
      icon: BuildingOfficeIcon,
      percentage: 15,
      description: 'Premium commercial properties, residential developments, and REITs.',
      companies: ['Commercial Properties', 'Residential Developments', 'Data Centers', 'Industrial Facilities'],
      color: 'bg-neutral-500'
    },
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: HeartIcon,
      percentage: 10,
      description: 'Biotechnology, medical devices, and healthcare technology solutions.',
      companies: ['Biotech Research', 'Medical Devices', 'Digital Health', 'Pharmaceuticals'],
      color: 'bg-error-500'
    },
    {
      id: 'energy',
      name: 'Clean Energy',
      icon: SunIcon,
      percentage: 10,
      description: 'Renewable energy projects, sustainable technologies, and clean energy infrastructure.',
      companies: ['Solar Power', 'Wind Energy', 'Energy Storage', 'Smart Grid'],
      color: 'bg-warning-500'
    },
    {
      id: 'manufacturing',
      name: 'Advanced Manufacturing',
      icon: WrenchScrewdriverIcon,
      percentage: 5,
      description: '3D printing, robotics, and smart manufacturing solutions.',
      companies: ['3D Printing', 'Robotics', 'IoT Manufacturing', 'Smart Factories'],
      color: 'bg-accent-500'
    },
    {
      id: 'ecommerce',
      name: 'E-Commerce',
      icon: ShoppingBagIcon,
      percentage: 5,
      description: 'Digital retail platforms, logistics, and e-commerce technology.',
      companies: ['Online Retail', 'Digital Marketplaces', 'Logistics Tech', 'Payment Solutions'],
      color: 'bg-secondary-400'
    },
    {
      id: 'logistics',
      name: 'Transportation & Logistics',
      icon: TruckIcon,
      percentage: 5,
      description: 'Electric vehicles, autonomous transportation, and smart logistics.',
      companies: ['Electric Vehicles', 'Autonomous Tech', 'Smart Logistics', 'Fleet Management'],
      color: 'bg-primary-400'
    },
    {
      id: 'crypto',
      name: 'Cryptocurrency',
      icon: CircleStackIcon,
      percentage: 5,
      description: 'Digital assets, DeFi protocols, and blockchain infrastructure.',
      companies: ['Bitcoin', 'Ethereum', 'DeFi Protocols', 'NFT Platforms'],
      color: 'bg-success-500'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  return (
    <div className="min-h-screen bg-background-paper">
      <Navigation />

      {/* Hero Section */}
      <motion.section 
        className="pt-28 pb-16 bg-gradient-to-br from-primary-700 to-primary-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              className="text-5xl font-bold text-white mb-6"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Our Investment Portfolio
            </motion.h1>
            <motion.div 
              className="flex justify-center"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
                Diversified investments across high-growth industries, delivering consistent returns through strategic asset allocation.
              </p>
            </motion.div>
            <motion.div
              className="mt-8"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/investment-plans">
                <button className="px-8 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-medium rounded-lg transition-colors duration-300 shadow-lg">
                  Explore Investments
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Portfolio Overview */}
      <motion.section 
        className="py-20 bg-gradient-to-b from-background-paper to-primary-50"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary-800 mb-4">Industry Allocation</h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Strategic distribution of assets across sectors positioned for long-term growth and stability
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => (
              <motion.div
                key={industry.id}
                className="bg-background-card rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-neutral-200"
                variants={itemVariants}
                whileHover={{ y: -5, scale: 1.02 }}
                onClick={() => setSelectedIndustry(industry)}
              >
                <div className={`h-2 ${industry.color}`} />
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-lg ${industry.color.replace('bg-', 'bg-opacity-20 text-')}`}>
                      <industry.icon className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-primary-800 ml-4">{industry.name}</h3>
                  </div>
                  <p className="text-neutral-600 mb-5 leading-relaxed">{industry.description}</p>
                  <div className="flex justify-between items-center">
                    <div className="text-3xl font-bold text-primary-600">{industry.percentage}%</div>
                    <div className="text-sm text-neutral-500">Portfolio Allocation</div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-neutral-200">
                    <div className="text-sm font-medium text-neutral-600 mb-2">Key Investments:</div>
                    <div className="flex flex-wrap gap-2">
                      {industry.companies.map((company, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${industry.color.replace('bg-', 'bg-opacity-15 text-')}`}
                        >
                          {company}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Portfolio Stats */}
      <motion.section 
        className="py-20 bg-primary-800 text-white relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_#ffffff,_transparent)]"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Portfolio Performance</h2>
            <p className="text-lg text-primary-100 max-w-2xl mx-auto">
              Consistent growth and returns across our diversified investment portfolio
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div 
              className="bg-primary-700 bg-opacity-40 rounded-xl p-6 backdrop-blur-sm border border-primary-600"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-secondary-400 mb-2">$500M+</div>
              <div className="text-sm text-primary-200">Assets Under Management</div>
            </motion.div>
            <motion.div 
              className="bg-primary-700 bg-opacity-40 rounded-xl p-6 backdrop-blur-sm border border-primary-600"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-secondary-400 mb-2">9</div>
              <div className="text-sm text-primary-200">Industry Sectors</div>
            </motion.div>
            <motion.div 
              className="bg-primary-700 bg-opacity-40 rounded-xl p-6 backdrop-blur-sm border border-primary-600"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-secondary-400 mb-2">150%</div>
              <div className="text-sm text-primary-200">Monthly Returns</div>
            </motion.div>
            <motion.div 
              className="bg-primary-700 bg-opacity-40 rounded-xl p-6 backdrop-blur-sm border border-primary-600"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-secondary-400 mb-2">24/7</div>
              <div className="text-sm text-primary-200">Portfolio Monitoring</div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Investment Process */}
      <motion.section 
        className="py-20 bg-gradient-to-b from-background-paper to-primary-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-primary-800 mb-4">Our Investment Process</h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Strategic approach to portfolio management and growth with a focus on long-term value creation
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: ChartBarIcon,
                title: "Market Analysis",
                description: "Continuous monitoring of market trends and opportunities across global markets",
                color: "bg-primary-500"
              },
              {
                icon: GlobeAltIcon,
                title: "Diversification",
                description: "Strategic allocation across multiple sectors and regions to minimize risk",
                color: "bg-secondary-500"
              },
              {
                icon: BeakerIcon,
                title: "Risk Management",
                description: "Advanced risk assessment and mitigation strategies for portfolio protection",
                color: "bg-accent-500"
              },
              {
                icon: RocketLaunchIcon,
                title: "Growth Optimization",
                description: "Maximizing returns through strategic positioning and timely adjustments",
                color: "bg-success-500"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="bg-background-card p-8 rounded-2xl shadow-lg border border-neutral-200"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * index }}
              >
                <div className={`p-4 rounded-xl ${step.color} bg-opacity-20 inline-flex mb-5`}>
                  <step.icon className={`h-8 w-8 ${step.color.replace('bg-', 'text-')}`} />
                </div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">{step.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Call to Action */}
      <motion.section
        className="py-20 bg-gradient-to-br from-primary-800 to-primary-900 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Invest in Your Future?</h2>
            <p className="text-lg text-primary-100 max-w-2xl mx-auto mb-8">
              Join our community of investors and gain access to our carefully curated portfolio of high-growth opportunities.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact">
                <button className="px-8 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-medium rounded-lg transition-colors duration-300 shadow-lg">
                  Schedule a Consultation
                </button>
              </Link>
            </div>
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}

export default Portfolio;